import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import type { ViewStore, ThemePref, FontSize } from "../stores/view";

interface HamburgerMenuProps {
  view: ViewStore;
}

const THEMES: { value: ThemePref; label: string }[] = [
  { value: "light",  label: "ライト" },
  { value: "dark",   label: "ダーク" },
  { value: "system", label: "システム" },
];

const SIZES: { value: FontSize; label: string }[] = [
  { value: "S", label: "小" },
  { value: "M", label: "中" },
  { value: "L", label: "大" },
];

export function HamburgerMenu(p: HamburgerMenuProps) {
  const [open, setOpen] = createSignal(false);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && open()) setOpen(false);
  };
  onMount(() => {
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <div class="fd-menu-root">
      <button
        type="button"
        aria-label="menu"
        aria-haspopup="menu"
        aria-expanded={open()}
        class="fd-menu-toggle"
        onClick={() => setOpen(!open())}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      <Show when={open()}>
        <div class="fd-menu-overlay" onClick={() => setOpen(false)} />
        <div role="menu" class="fd-menu-panel">
          <div class="fd-menu-section" role="radiogroup" aria-label="theme">
            <span class="fd-menu-section-label">テーマ</span>
            <For each={THEMES}>
              {(t) => (
                <label class="fd-menu-radio-label">
                  <input
                    type="radio"
                    name="fd-theme"
                    value={t.value}
                    checked={p.view.theme() === t.value}
                    onChange={() => p.view.setTheme(t.value)}
                  />
                  {t.label}
                </label>
              )}
            </For>
          </div>

          <div class="fd-menu-section">
            <span class="fd-menu-section-label">表示</span>
            <label class="fd-menu-checkbox-label">
              <input
                type="checkbox"
                checked={p.view.wrap()}
                onChange={(e) => p.view.setWrap(e.currentTarget.checked)}
              />
              行を折り返す
            </label>
            <label class="fd-menu-checkbox-label">
              <input
                type="checkbox"
                checked={p.view.syntaxHighlight()}
                onChange={(e) => p.view.setSyntaxHighlight(e.currentTarget.checked)}
              />
              シンタックスハイライト
            </label>
            <label class="fd-menu-checkbox-label">
              <input
                type="checkbox"
                checked={p.view.minimap()}
                onChange={(e) => p.view.setMinimap(e.currentTarget.checked)}
              />
              ミニマップ
            </label>
          </div>

          <div class="fd-menu-section">
            <span class="fd-menu-section-label">文字サイズ</span>
            <div class="fd-menu-button-row">
              <For each={SIZES}>
                {(s) => (
                  <button
                    type="button"
                    class={
                      "fd-menu-size-btn" +
                      (p.view.fontSize() === s.value ? " fd-menu-size-active" : "")
                    }
                    onClick={() => p.view.setFontSize(s.value)}
                  >
                    {s.label}
                  </button>
                )}
              </For>
            </div>
          </div>

          <div class="fd-menu-section">
            <span class="fd-menu-section-label">パフォーマンス</span>
            <label class="fd-menu-checkbox-label">
              <input
                type="checkbox"
                checked={p.view.showDetailedTimings()}
                onChange={(e) => p.view.setShowDetailedTimings(e.currentTarget.checked)}
              />
              詳細タイミングを表示
            </label>
          </div>
        </div>
      </Show>
    </div>
  );
}
